import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, X, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const brochures = [
  {
    id: "bright-perfect",
    title: "Bright Perfect Heavy Equipment",
    description: "Complete catalog of heavy equipment spare parts, services, and solutions offered by Bright Perfect.",
    image: "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=600",
    file: "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/3g1ugq3i_BRIGHT%20PERFECT%20HEAVY%20EQUIPMENT-compressed.pdf",
    fileSize: "2.5 MB",
    pages: "12 Pages"
  },
  {
    id: "hayalogix",
    title: "HayalogiX Transport & Equipment",
    description: "Explore our transport and equipment rental services, fleet details, and logistics solutions.",
    image: "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=600",
    file: "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/sepx716m_hayalogix_flyer_A4_Print.pdf",
    fileSize: "1.8 MB",
    pages: "8 Pages"
  },
  {
    id: "hammer-mount",
    title: "HAMMER MOUNT General Contracting Services",
    description: "Discover our comprehensive general contracting services, construction solutions, and project management expertise.",
    image: "https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=600",
    file: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/mtojvazy_HAMMER%20MOUNT.pdf",
    fileSize: "2.0 MB",
    pages: "10 Pages"
  }
];

const DownloadForm = ({ brochure, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Thank you! Your download will start shortly.");
    setIsSubmitting(false);
    onSuccess(brochure);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white max-w-lg w-full p-8 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="close-form-btn"
        >
          <X size={24} />
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#F5B800] flex items-center justify-center">
              <FileText size={24} className="text-black" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900">Download Brochure</h3>
              <p className="text-sm text-gray-500">{brochure.title}</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm">
            Please fill in your details to download the brochure. We'll also keep you updated on our latest products and offers.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="download-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              data-testid="form-name"
              className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="form-email"
              className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
              placeholder="john@company.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                data-testid="form-phone"
                className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
                placeholder="+971 XX XXX XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                data-testid="form-country"
                className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
                placeholder="UAE"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              data-testid="form-company"
              className="w-full border border-gray-300 focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800] px-4 py-3 outline-none transition-colors"
              placeholder="Your Company"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="submit-download-btn"
            className="w-full bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold py-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
          >
            {isSubmitting ? "Processing..." : "Download Brochure"}
            {!isSubmitting && <Download size={18} />}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export const Products = () => {
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleDownloadClick = (brochure) => {
    setSelectedBrochure(brochure);
    setShowForm(true);
  };

  const handleFormSuccess = (brochure) => {
    setShowForm(false);
    // Trigger download
    const link = document.createElement('a');
    link.href = brochure.file;
    link.download = brochure.title + '.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gray-900 py-20 md:py-28" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#F5B800] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Our Products
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Product Catalogs & Brochures
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Download our comprehensive product catalogs and brochures to explore our full range of heavy equipment, spare parts, and transport services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brochures Section */}
      <section className="py-16 md:py-24" data-testid="brochures-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Download Our Brochures
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get detailed information about our products and services. Fill in your details to receive instant access to our catalogs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brochures.map((brochure, index) => (
              <motion.div
                key={brochure.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group"
                data-testid={`brochure-card-${brochure.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={brochure.image} 
                    alt={brochure.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white/80 text-sm">
                      <FileText size={16} />
                      <span>{brochure.pages}</span>
                      <span className="mx-2">•</span>
                      <span>{brochure.fileSize}</span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
                    {brochure.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {brochure.description}
                  </p>
                  <button
                    onClick={() => handleDownloadClick(brochure)}
                    data-testid={`download-btn-${brochure.id}`}
                    className="w-full bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold py-3 transition-all flex items-center justify-center gap-2"
                  >
                    <Download size={18} />
                    Download Brochure
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="info-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="w-16 h-16 bg-[#F5B800] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-black" />
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Need a Custom Quotation?
          </h3>
          <p className="text-gray-600 mb-8">
            Can't find what you're looking for in our brochures? Contact us directly for custom requirements and personalized quotations.
          </p>
          <a
            href="https://wa.me/971563419000"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta"
            className="bg-[#25D366] text-white hover:bg-[#20BA5C] font-bold px-8 py-4 transition-all inline-flex items-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Chat on WhatsApp
          </a>
        </div>
      </section>

      {/* Download Form Modal */}
      {showForm && selectedBrochure && (
        <DownloadForm
          brochure={selectedBrochure}
          onClose={() => setShowForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};
